/**
 * ImportMealPlanModal Component
 * Development/testing tool for importing meal plans from JSON files
 */

import React, { useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
    mealPlanApi,
    CreateMealPlanData,
    Profile,
} from "../services/api";
import { ProfileCombobox } from "./ProfileCombobox";

interface ImportMealPlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImportSuccess?: () => void;
}

export const ImportMealPlanModal: React.FC<ImportMealPlanModalProps> = ({
    isOpen,
    onClose,
    onImportSuccess,
}) => {
    const { dbUser, profiles } = useAuth();
    const [mealPlanName, setMealPlanName] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedProfile, setSelectedProfile] = useState<Profile | null>(
        null
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const resetForm = () => {
        setMealPlanName("");
        setSelectedFile(null);
        setSelectedProfile(null);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (
                file.type === "application/json" ||
                file.name.endsWith(".json")
            ) {
                setSelectedFile(file);
                setError(null);

                // Auto-populate name from filename if empty
                if (!mealPlanName) {
                    const nameFromFile = file.name.replace(/\.json$/i, "");
                    setMealPlanName(nameFromFile);
                }
            } else {
                setError("Please select a valid JSON file");
                setSelectedFile(null);
            }
        }
    };

    const handleProfileSelect = (profile: Profile | null) => {
        setSelectedProfile(profile);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!dbUser?.id) {
            setError("User not authenticated");
            return;
        }

        if (!selectedFile) {
            setError("Please select a JSON file");
            return;
        }

        if (!mealPlanName.trim()) {
            setError("Please enter a meal plan name");
            return;
        }

        if (!selectedProfile) {
            setError("Please select a profile");
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Read the JSON file
            const fileContent = await readFileAsText(selectedFile);
            let planData: any;

            try {
                planData = JSON.parse(fileContent);
            } catch (parseError) {
                throw new Error("Invalid JSON file format");
            }

            // Create the meal plan with the correct backend structure
            const createData: CreateMealPlanData = {
                name: mealPlanName.trim(),
                profiles: [selectedProfile.id], // Array of profile IDs
                plan: {
                    ...planData,
                    // Add metadata to the plan
                    imported_at: new Date().toISOString(),
                    imported_from: selectedFile.name,
                },
            };

            await mealPlanApi.createMealPlan(dbUser.id, createData);

            // Success!
            handleClose();
            onImportSuccess?.();
        } catch (err) {
            console.error("Error importing meal plan:", err);
            setError(
                err instanceof Error
                    ? err.message
                    : "Failed to import meal plan"
            );
        } finally {
            setLoading(false);
        }
    };

    const readFileAsText = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (e.target?.result) {
                    resolve(e.target.result as string);
                } else {
                    reject(new Error("Failed to read file"));
                }
            };
            reader.onerror = () => reject(new Error("Error reading file"));
            reader.readAsText(file);
        });
    };

    if (!isOpen) return null;

    // Styles
    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
    };

    const modalStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "32px",
        maxWidth: "540px",
        width: "90%",
        maxHeight: "90vh",
        overflow: "auto",
        boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    };

    const headerStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "24px",
        paddingBottom: "20px",
        borderBottom: "1px solid #e5e7eb",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "24px",
        fontWeight: "600",
        color: "#1f2937",
        margin: 0,
    };

    const closeButtonStyle: React.CSSProperties = {
        background: "none",
        border: "none",
        fontSize: "24px",
        cursor: "pointer",
        color: "#6b7280",
        padding: "4px",
        borderRadius: "4px",
        width: "32px",
        height: "32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    const formStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        gap: "24px",
    };

    const fieldStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
    };

    const labelStyle: React.CSSProperties = {
        fontSize: "14px",
        fontWeight: "600",
        color: "#374151",
        display: "flex",
        alignItems: "center",
        gap: "4px",
    };

    const requiredStyle: React.CSSProperties = {
        color: "#dc2626",
        fontSize: "14px",
    };

    const inputStyle: React.CSSProperties = {
        padding: "12px 16px",
        border: "1px solid #d1d5db",
        borderRadius: "8px",
        fontSize: "14px",
        outline: "none",
        transition: "all 0.2s",
        backgroundColor: "white",
    };

    const fileInputStyle: React.CSSProperties = {
        flex: 1,
        padding: "10px 14px",
        border: "1px solid #d1d5db",
        borderRadius: "8px",
        fontSize: "14px",
        cursor: "pointer",
    };

    const buttonGroupStyle: React.CSSProperties = {
        display: "flex",
        gap: "12px",
        justifyContent: "flex-end",
        marginTop: "8px",
        paddingTop: "24px",
        borderTop: "1px solid #e5e7eb",
    };

    const cancelButtonStyle: React.CSSProperties = {
        padding: "12px 24px",
        border: "1px solid #d1d5db",
        borderRadius: "8px",
        backgroundColor: "white",
        color: "#374151",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.2s",
    };

    const submitButtonStyle: React.CSSProperties = {
        padding: "12px 24px",
        border: "none",
        borderRadius: "8px",
        backgroundColor: "#3b82f6",
        color: "white",
        cursor: loading ? "not-allowed" : "pointer",
        fontSize: "14px",
        fontWeight: "500",
        opacity: loading ? 0.6 : 1,
        transition: "all 0.2s",
    };

    const errorStyle: React.CSSProperties = {
        backgroundColor: "#fef2f2",
        border: "1px solid #fecaca",
        color: "#dc2626",
        padding: "12px 16px",
        borderRadius: "8px",
        fontSize: "14px",
        marginBottom: "16px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };

    const fileInfoStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#6b7280",
        marginTop: "6px",
        lineHeight: "1.4",
    };

    const selectedFileStyle: React.CSSProperties = {
        marginTop: "8px",
        padding: "8px 12px",
        backgroundColor: "#f0f9ff",
        border: "1px solid #bae6fd",
        borderRadius: "6px",
        fontSize: "12px",
        color: "#0369a1",
        display: "flex",
        alignItems: "center",
        gap: "6px",
    };

    return (
        <div style={overlayStyle} onClick={handleClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <div style={headerStyle}>
                    <h2 style={titleStyle}>Import Meal Plan</h2>
                    <button
                        onClick={handleClose}
                        style={closeButtonStyle}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f3f4f6";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                                "transparent";
                        }}
                    >
                        ×
                    </button>
                </div>

                {error && (
                    <div style={errorStyle}>
                        <span>⚠️</span>
                        <div>
                            <strong>Error:</strong> {error}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} style={formStyle}>
                    <div style={fieldStyle}>
                        <label style={labelStyle}>
                            Meal Plan Name
                            <span style={requiredStyle}>*</span>
                        </label>
                        <input
                            type="text"
                            value={mealPlanName}
                            onChange={(e) => setMealPlanName(e.target.value)}
                            placeholder="Enter a name for this meal plan"
                            style={inputStyle}
                            onFocus={(e) => {
                                e.currentTarget.style.borderColor = "#3b82f6";
                                e.currentTarget.style.boxShadow =
                                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                            }}
                            onBlur={(e) => {
                                e.currentTarget.style.borderColor = "#d1d5db";
                                e.currentTarget.style.boxShadow = "none";
                            }}
                            disabled={loading}
                        />
                    </div>

                    <div style={fieldStyle}>
                        <label style={labelStyle}>
                            Profile
                            <span style={requiredStyle}>*</span>
                        </label>
                        <ProfileCombobox
                            profiles={profiles}
                            selectedProfile={selectedProfile}
                            onProfileSelect={handleProfileSelect}
                            placeholder="Select a profile for this meal plan"
                            disabled={loading}
                        />
                        <div style={fileInfoStyle}>
                            Choose which profile this meal plan will be
                            associated with
                        </div>
                    </div>

                    <div style={fieldStyle}>
                        <label style={labelStyle}>
                            JSON File
                            <span style={requiredStyle}>*</span>
                        </label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json,application/json"
                            onChange={handleFileChange}
                            style={fileInputStyle}
                            disabled={loading}
                        />
                        <div style={fileInfoStyle}>
                            Select a JSON file containing meal plan data
                        </div>
                        {selectedFile && (
                            <div style={selectedFileStyle}>
                                <span>✓</span>
                                <div>
                                    <strong>{selectedFile.name}</strong> (
                                    {(selectedFile.size / 1024).toFixed(1)} KB)
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={buttonGroupStyle}>
                        <button
                            type="button"
                            onClick={handleClose}
                            style={cancelButtonStyle}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    "#f9fafb";
                                e.currentTarget.style.borderColor = "#9ca3af";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "white";
                                e.currentTarget.style.borderColor = "#d1d5db";
                            }}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={submitButtonStyle}
                            onMouseEnter={(e) => {
                                if (!loading) {
                                    e.currentTarget.style.backgroundColor =
                                        "#2563eb";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!loading) {
                                    e.currentTarget.style.backgroundColor =
                                        "#3b82f6";
                                }
                            }}
                            disabled={
                                loading ||
                                !selectedFile ||
                                !mealPlanName.trim() ||
                                !selectedProfile
                            }
                        >
                            {loading ? "Importing..." : "Import Meal Plan"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
